import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { DocumentService } from "@/services/api/DocumentService";
import { assessDocumentHealth } from "@/lib/knowledge/documentHealth";
import { buildKnowledgeBrain } from "@/lib/knowledge/topicClusters";
import type { PaginationResult } from "@/lib/pagination";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

/**
 * How many documents the workspace-wide brain overview reads. One page is
 * enough to cluster a realistic knowledge base without pulling the whole
 * library into the client; the file tools below the fold keep their own
 * paginated query.
 */
const BRAIN_PAGE_SIZE = 200;

/**
 * Hard limit baked into `DocumentService.getKnowledgeBaseDocuments` (the
 * non-paginated fetch a per-Genie page uses, e.g.
 * `useDocuments("my-documents", { agentId })`). That call reports no `total`,
 * so if a supplied `documents` array lands exactly on this ceiling we cannot
 * prove it is complete — `isPartial` treats that boundary as "possibly
 * partial" rather than claiming completeness it has no way to verify. Keep
 * this in sync with the `limit: 500` in `src/services/api/DocumentService.ts`.
 */
const SUPPLIED_DOCUMENTS_HARD_CAP = 500;

interface UseKnowledgeBrainOptions {
  userId?: string;
  /** Failed caller questions, used to mark clusters with open questions. */
  openQuestions?: string[];
  /**
   * Pre-fetched documents to cluster instead of running the internal
   * workspace-wide query — e.g. a per-Genie page that already holds the
   * result of `useDocuments("my-documents", { agentId: genieId })`. Passing
   * this (even as an empty array, once the caller's own fetch has settled)
   * puts the hook in "scoped" mode: the internal 200-doc workspace query
   * never fires, so the page only pays for the query it already owns instead
   * of a second, overlapping one (finding 6,
   * docs/reviews/knowledge-brain-report.md). Omit it entirely to keep the
   * original workspace-wide behaviour.
   */
  documents?: DocumentWithMetadata[];
}

/** Server's definition of "in the knowledge base" (see `handlers/knowledge-base/all.ts`'s `inKBCount`): has an `el_doc_id`. Replicated here for scoped mode, which gets no server-computed count to read. */
const isInKnowledgeBase = (doc: DocumentWithMetadata) => Boolean(doc.el_doc_id);

/**
 * The Genie's knowledge, as topics rather than files.
 *
 * Clustering is a client-side approximation over document names, descriptions
 * and folders — see `src/lib/knowledge/topicClusters.ts` for exactly what it can
 * and cannot see.
 *
 * Two modes:
 *  - **Workspace-wide** (default): runs its own paginated query over up to
 *    `BRAIN_PAGE_SIZE` documents for `userId`.
 *  - **Scoped** (`documents` supplied): clusters exactly the given array and
 *    runs no query of its own. Intended for a per-Genie page that already
 *    holds that Genie's documents.
 */
export const useKnowledgeBrain = ({
  userId,
  openQuestions = [],
  documents: suppliedDocuments,
}: UseKnowledgeBrainOptions) => {
  const scoped = suppliedDocuments !== undefined;

  // Mirrors `usePaginatedDocuments("my-documents", BRAIN_PAGE_SIZE, {}, userId)`
  // (`src/hooks/documents/useDocumentFetch.ts`), called directly rather than
  // through that wrapper so this hook can gate it with `enabled: !scoped` —
  // the wrapper does not expose an `enabled` option today. When `documents`
  // is supplied this query never runs; that is the point of scoped mode.
  const query = useInfiniteQuery({
    queryKey: ["knowledge-brain-workspace-documents", userId, BRAIN_PAGE_SIZE],
    queryFn: async ({ pageParam = 1 }): Promise<PaginationResult<DocumentWithMetadata>> => {
      return await DocumentService.getKnowledgeBaseDocumentsPaginated(
        userId,
        pageParam,
        BRAIN_PAGE_SIZE,
        {},
        false,
      );
    },
    getNextPageParam: (lastPage) => {
      return lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    initialPageParam: 1,
    enabled: !scoped,
  });

  const workspaceDocuments = useMemo<DocumentWithMetadata[]>(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  const documents = scoped ? suppliedDocuments : workspaceDocuments;

  const totalDocuments = scoped
    ? documents.length
    : (query.data?.pages[0]?.total ?? documents.length);

  const inKnowledgeBaseCount = scoped
    ? documents.filter(isInKnowledgeBase).length
    : (query.data?.pages[0]?.inKBCount ?? 0);

  const brain = useMemo(
    () => buildKnowledgeBrain(documents, { openQuestions }),
    [documents, openQuestions],
  );

  const health = useMemo(() => assessDocumentHealth(documents), [documents]);

  // Scoped mode has no server-reported total to compare against, so it can
  // only ever notice the one truncation signal it has: landing exactly on
  // the caller's own fetch's hard cap (see `SUPPLIED_DOCUMENTS_HARD_CAP`
  // above). It cannot detect partiality any other way — it never claims
  // completeness it hasn't verified, but it also cannot rule out truncation
  // below that ceiling.
  const isPartial = scoped
    ? documents.length >= SUPPLIED_DOCUMENTS_HARD_CAP
    : totalDocuments > documents.length;

  return {
    documents,
    totalDocuments,
    inKnowledgeBaseCount,
    waitingToActivateCount: Math.max(totalDocuments - inKnowledgeBaseCount, 0),
    clusters: brain.clusters,
    summary: brain.summary,
    health,
    isLoading: scoped ? false : query.isLoading,
    /** True when the clustering only saw part of the library. */
    isPartial,
  };
};
